import { useLayoutEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import sanitizeHtml from "sanitize-html";
import useApi from "@/contexts/ApiContext";
import { sanitizeBannerHtmlOptions } from "@/utils/sanitize.utils";
import AcknowledgementModal from "@/components/shared-ui/AcknowledgementModal";
import * as Styled from "./styled";

const GlobalBanner = () => {
  const api = useApi();
  const ref = useRef<HTMLDivElement>(null);

  const { data: banner } = useQuery({
    queryKey: ["get-banner"],
    queryFn: () => api.banner.get(),
    staleTime: 10 * 60 * 1000,
    throwOnError: false,
  });

  const visible = !!banner?.visible;

  // Header and Page are fixed/sticky-positioned, and live in a completely
  // separate component tree from this banner, so there's no prop/context
  // path to tell them how tall the banner is. Instead this publishes the
  // banner's rendered height as a CSS variable (--banner-height) on <html>,
  // which Header/Page read via var(--banner-height, 0px) to push their own
  // fixed offsets down - no re-renders needed on either side, and they
  // don't need to know the banner exists.
  //
  // ref + ResizeObserver: the height isn't a fixed constant - it depends on
  // the banner's content/padding and can change if content wraps (e.g. on
  // a narrower viewport), so it's measured off the actual DOM node rather
  // than hardcoded, and re-measured whenever its size changes.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!visible || !ref.current) {
      root.style.setProperty("--banner-height", "0px");
      return;
    }
    const el = ref.current;
    const setHeight = () =>
      root.style.setProperty("--banner-height", `${el.offsetHeight}px`);
    setHeight();
    const observer = new ResizeObserver(setHeight);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.setProperty("--banner-height", "0px");
    };
  }, [visible]);

  if (!banner?.visible) {
    return null;
  }

  const sanitizedContent = sanitizeHtml(
    banner.content,
    sanitizeBannerHtmlOptions,
  );

  return (
    <>
      <Styled.Banner ref={ref} role="status" aria-live="polite">
        <Styled.BannerContent>
          <span dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
        </Styled.BannerContent>
      </Styled.Banner>
      {banner.modal && (
        <AcknowledgementModal key={banner.modal.id} modal={banner.modal} />
      )}
    </>
  );
};

export default GlobalBanner;
